import { Autocomplete, Box, Chip, Link, Stack, TextField, Tooltip, Typography } from "@mui/material";
import InfoIcon from "atoms/InfoIcon";
import { ExtendedDataProvider } from "misc/dataProvider.types";
import {
  DuplicateEmailRecipientRecordType,
  EmailContactInfoTypeEnum,
  UndeliverableEmailRecordType,
} from "pages/SharedRecords.types";
import { Fragment, useEffect, useMemo, useState } from "react";
import { TextInput, useDataProvider, useTranslate } from "react-admin";
import { useFormContext } from "react-hook-form";

import DuplicateRecipientsAlert from "./DuplicateRecipientsAlert";
import { buildContactTypeOptions } from "./recipientOptions";
import { EmailReceiverListPropsType, RecipientOptionType } from "./SendEmailDialog.types";
import UndeliverableEmailAlert from "./UndeliverableEmailsAlert";

/**
 * The option that should be active when the dialog opens (or when the selected template
 * changes): the one matching the template's configured recipient role and the address
 * kind the template implies, falling back to the first option.
 */
function seedOption(recipientOptions: RecipientOptionType[], templateRole?: string | null, preferInvoice?: boolean) {
  const preferredType = preferInvoice ? EmailContactInfoTypeEnum.INVOICE : EmailContactInfoTypeEnum.COMMUNICATION;
  const byRoleAndType = recipientOptions.find(
    (option) => option.role === templateRole && option.contactInfoType === preferredType,
  );
  const byType = recipientOptions.find((option) => option.contactInfoType === preferredType);

  return byRoleAndType ?? byType ?? recipientOptions[0];
}

function optionLabel(option: RecipientOptionType) {
  return option.recipients.length > 1 ? `${option.label} (${option.recipients.length})` : option.label;
}

export default function EmailReceiverList(props: EmailReceiverListPropsType) {
  const { recipients, useInvoiceContactTypeInitially, recipientOptions, templateRecipientRole, templateId } = props;
  const translate = useTranslate();
  const [showCcField, setShowCcField] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [undeliverableEmails, setUndeliverableEmails] = useState<UndeliverableEmailRecordType[]>([]);
  const [duplicateRecipients, setDuplicateRecipients] = useState<DuplicateEmailRecipientRecordType[]>([]);
  const { setValue } = useFormContext();
  const [allRecipientNames, setAllRecipientNames] = useState<string[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [recipientsExpanded, setRecipientsExpanded] = useState(false);
  const dataProvider = useDataProvider<ExtendedDataProvider>();

  const availableOptions = useMemo(
    () => (recipientOptions?.length ? recipientOptions : buildContactTypeOptions(recipients, translate)),
    [recipientOptions, recipients, translate],
  );

  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(() => {
    const seeded = seedOption(availableOptions, templateRecipientRole, useInvoiceContactTypeInitially);
    return seeded ? [seeded.id] : [];
  });

  const selectedOptions = useMemo(
    () => availableOptions.filter((option) => selectedOptionIds.includes(option.id)),
    [availableOptions, selectedOptionIds],
  );
  const finalRecipients = useMemo(() => selectedOptions.flatMap((option) => option.recipients), [selectedOptions]);

  // Call sites rebuild their options on every render, so compare by value.
  const recipientsKey = JSON.stringify(finalRecipients);

  const hiddenFromDisplayCount = totalCount - allRecipientNames.length;
  const displayText =
    allRecipientNames.join("; ") +
    (hiddenFromDisplayCount > 0 ? ` … (${translate("countMore", { count: hiddenFromDisplayCount })})` : "");

  useEffect(() => {
    setValue("recipients", customMode ? [] : finalRecipients);
    if (!customMode) setValue("custom_to", undefined);
  }, [recipientsKey, customMode]);

  useEffect(() => {
    if (customMode) return;

    const validateRecipients = async () => {
      try {
        const res = await dataProvider.validateEmailRecipients(finalRecipients, templateId);
        setUndeliverableEmails(res.data);
        setDuplicateRecipients(res.duplicate_recipients);
        setAllRecipientNames(res.recipient_names);
        setTotalCount(res.total_count);
      } catch {
        setUndeliverableEmails([]);
        setDuplicateRecipients([]);
      }
    };
    validateRecipients();
  }, [recipientsKey, templateId, customMode]);

  // A newly selected template can carry its own default recipient role / address kind.
  useEffect(() => {
    const seeded = seedOption(availableOptions, templateRecipientRole, useInvoiceContactTypeInitially);
    if (seeded) setSelectedOptionIds([seeded.id]);
  }, [templateRecipientRole, useInvoiceContactTypeInitially]);

  const selectOptions = (selection: RecipientOptionType[]) => {
    // Never end up with nothing to send to.
    if (selection.length === 0) return;

    setSelectedOptionIds(selection.map((option) => option.id));
  };

  return (
    <Stack gap={2}>
      {!customMode && (
        <>
          <UndeliverableEmailAlert undeliverableEmails={undeliverableEmails} />
          <DuplicateRecipientsAlert duplicateRecipients={duplicateRecipients} />
        </>
      )}

      <Box>
        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
          <Link onClick={() => setCustomMode((prev) => !prev)} sx={{ cursor: "pointer" }}>
            {translate(customMode ? "emailing.use_recipient_selection" : "emailing.use_custom_email")}
          </Link>

          {!showCcField && (
            <Link onClick={() => setShowCcField(true)} sx={{ cursor: "pointer" }}>
              <Box display="flex" alignItems="center">
                Cc
                <Tooltip title={translate("emailing.cc_info")}>
                  <Box component="span" display="flex">
                    <InfoIcon fontSize="small" />
                  </Box>
                </Tooltip>
              </Box>
            </Link>
          )}
        </Box>

        {customMode ? (
          <TextInput source="custom_to" required sx={{ mt: 1 }} />
        ) : (
          <Fragment>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disableClearable
              options={availableOptions}
              value={selectedOptions}
              onChange={(_, selection) => selectOptions(selection)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={optionLabel}
              renderTags={(tags, getTagProps) =>
                tags.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return <Chip key={key} size="small" label={optionLabel(option)} {...tagProps} />;
                })
              }
              renderInput={(params) => <TextField {...params} label={translate("resources.emails.fields.to")} />}
            />

            <Box sx={{ mt: 1, mb: 3 }}>
              <Link
                onClick={() => setRecipientsExpanded((prev) => !prev)}
                sx={{ cursor: "pointer", display: "block", fontSize: "0.8rem" }}
              >
                {recipientsExpanded
                  ? translate("actions.show_less")
                  : translate("emailing.show_recipient_addresses", totalCount)}
              </Link>
              {recipientsExpanded && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  {displayText ? displayText : "..."}
                </Typography>
              )}
            </Box>
          </Fragment>
        )}
        {showCcField && <TextInput source="cc" label="Cc" />}
      </Box>
    </Stack>
  );
}
